import React, { FC, useEffect, useMemo } from 'react';
import {
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from '@duxton-web/ui';
import { CheckboxChangeEvent } from '@duxton-web/ui/es/checkbox';
import scribe from '@grab-x/scribe-web-sdk';
import { styled } from 'styled-components';

import SectionCollapse from '../common/SectionCollapse';
import { useVariables } from '../contexts/ExperimentFormInfoProvider';
import BaseModal from './BaseModal';

import './EditExperimentModal.css';

import { ExpFormField } from '../helper/consts';
import { getAllDomains, getAllDomainsNames } from '@/common/domains';
import { OwnersAutoComplete } from '@/common/OwnersAutoComplete';
import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import Service, { IsAlphaNumericWithUnderscore } from '@/lib/App';
import { EXPERIMENT_SAMPLE_TEMPLATE_URL } from '@/utils/url';

const SKIP_DOMAIN_FIELD = 'skipDomain';

interface EditExperimentModalProps {
  initializedCloneExperiment?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
  isCloneMode?: boolean;
  isEditMode?: boolean;
}

export function getAssignByFacetFromDomain(domainName: string) {
  const assignByFacet = getAllDomains().find(
    (domain) => domain.name === domainName,
  )?.targetBy;

  return Service.cache
    ?.getAllFacets()
    .find((facet) => facet.name === assignByFacet);
}

const EditExperimentModal: FC<EditExperimentModalProps> = ({
  onOk,
  onCancel,
  initializedCloneExperiment,
  isCloneMode,
  isEditMode,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setVariables] = useVariables();
  const createExperimentForm = Form.useFormInstance();

  const createExperimentFormValues = Form.useWatch([], {
    form: createExperimentForm,
    preserve: true,
  });

  const isUninitializedForm = !createExperimentForm.getFieldValue(
    ExpFormField.NAME,
  );

  useEffect(() => {
    if (isUninitializedForm) {
      scribe.sendEvent(newCreationFlowEvents.viewCreateExperimentModal.name, {
        STATE_NAME: newCreationFlowEvents.viewCreateExperimentModal.state,
      });
    }
  }, []);

  const [experimentMetadataForm] = Form.useForm();

  const experimentMetadataFormValues = Form.useWatch([], {
    form: experimentMetadataForm,
    preserve: true,
  });

  const contentChanged = useMemo(() => {
    if (!createExperimentFormValues || !experimentMetadataFormValues) {
      return false;
    }
    const { name, description, domain, productManager, productAnalyst } =
      createExperimentFormValues;
    const {
      name: metadataName,
      description: metadataDescription,
      domain: metadataDomain,
      productManager: metadataProductManager,
      productAnalyst: metadataProductAnalyst,
      skipDomain: metadataSkipDomain,
    } = experimentMetadataFormValues;
    if (
      name !== metadataName ||
      description !== metadataDescription ||
      domain !== metadataDomain ||
      productManager !== metadataProductManager ||
      productAnalyst !== metadataProductAnalyst ||
      (metadataSkipDomain === true && metadataDomain !== undefined)
    ) {
      return true;
    }
    return false;
  }, [experimentMetadataFormValues, createExperimentFormValues]);

  const selectedDomainName = Form.useWatch(ExpFormField.DOMAIN, {
    form: experimentMetadataForm,
  });

  const assignByFacetName =
    getAssignByFacetFromDomain(selectedDomainName)?.title;

  const initialMetadataValues = {
    name: createExperimentForm.getFieldValue(ExpFormField.NAME),
    description: createExperimentForm.getFieldValue(ExpFormField.DESCRIPTION),
    domain: createExperimentForm.getFieldValue(ExpFormField.DOMAIN),
    productManager: createExperimentForm.getFieldValue(
      ExpFormField.PRODUCT_MANAGER,
    ),
    productAnalyst: createExperimentForm.getFieldValue(
      ExpFormField.PRODUCT_ANALYST,
    ),
    skipDomain:
      !!createExperimentForm.getFieldValue(ExpFormField.NAME) &&
      !createExperimentForm.getFieldValue(ExpFormField.DOMAIN),
  };

  const showActiveDomainOnly = true;
  const domainValues = getAllDomainsNames(showActiveDomainOnly).map((item) => ({
    label: item,
    value: item,
  }));

  const handleEditExperiment = () => {
    experimentMetadataForm.validateFields().then(
      () => {
        const resetFormSections = () => {
          const resetField = (field, value) => {
            createExperimentForm.setFieldValue(field, value);
          };
          setVariables([]);
          // when clone experiment, there will be initial values
          // resetFields would revert to the initial values, not reverting to empty values (unlike its name)
          // here we properly set certain fields to empty when changing domain
          // meanwhile we preserve user's change for fields like start time and end time, which wont force them to re-enter
          const nullableFields = [
            ExpFormField.VARIABLES.ROOT,
            ExpFormField.STRATEGY.ROOT,
            ExpFormField.TARGET.TYPE,
            ExpFormField.TARGET.VALUE,
            ExpFormField.TARGET.GROUP,
            ExpFormField.SAMPLE_SIZE,
          ];
          const arrayFields = [ExpFormField.CONSTRAINTS];
          nullableFields.forEach((field) => resetField(field, null));
          arrayFields.forEach((field) => resetField(field, []));
        };
        const populateToCreateExperimentForm = () => {
          createExperimentForm.setFieldsValue({
            ...createExperimentForm.getFieldsValue(true),
            name: experimentMetadataForm.getFieldValue(ExpFormField.NAME),
            description: experimentMetadataForm.getFieldValue(
              ExpFormField.DESCRIPTION,
            ),
            domain: experimentMetadataForm.getFieldValue(ExpFormField.DOMAIN),
            productManager: experimentMetadataForm.getFieldValue(
              ExpFormField.PRODUCT_MANAGER,
            ),
            productAnalyst: experimentMetadataForm.getFieldValue(
              ExpFormField.PRODUCT_ANALYST,
            ),
          });
        };

        if (isUninitializedForm) {
          scribe.sendEvent(
            newCreationFlowEvents.clickCreateExperimentModal.name,
            {
              name: experimentMetadataForm.getFieldValue(ExpFormField.NAME),
              description: experimentMetadataForm.getFieldValue(
                ExpFormField.DESCRIPTION,
              ),
              domain: experimentMetadataForm.getFieldValue(ExpFormField.DOMAIN),
              productManager: experimentMetadataForm.getFieldValue(
                ExpFormField.PRODUCT_MANAGER,
              ),
              productAnalyst: experimentMetadataForm.getFieldValue(
                ExpFormField.PRODUCT_ANALYST,
              ),
              STATE_NAME:
                newCreationFlowEvents.clickCreateExperimentModal.state,
            },
          );
        }

        const currentDomain = createExperimentForm.getFieldValue(
          ExpFormField.DOMAIN,
        );
        const newDomain = experimentMetadataForm.getFieldValue(
          ExpFormField.DOMAIN,
        );

        if (currentDomain && currentDomain !== newDomain) {
          Modal.confirm({
            title: 'Are you sure you want to change the domain?',
            content: 'You will lose all content you added',
            okText: 'Yes',
            cancelText: 'No',
            onOk() {
              populateToCreateExperimentForm();
              resetFormSections();
              onOk?.();
            },
            onCancel() {
              return;
            },
          });
        } else {
          populateToCreateExperimentForm();
          onOk?.();
        }
      },
      () => {
        return;
      },
    );
  };

  const handleCancel = () => {
    scribe.sendEvent(newCreationFlowEvents.clickCancelExperimentModal.name, {
      name: experimentMetadataForm.getFieldValue(ExpFormField.NAME),
      description: experimentMetadataForm.getFieldValue(
        ExpFormField.DESCRIPTION,
      ),
      domain: experimentMetadataForm.getFieldValue(ExpFormField.DOMAIN),
      productManager: experimentMetadataForm.getFieldValue(
        ExpFormField.PRODUCT_MANAGER,
      ),
      productAnalyst: experimentMetadataForm.getFieldValue(
        ExpFormField.PRODUCT_ANALYST,
      ),
      cancelType: isUninitializedForm ? 'create' : 'edit',
      STATE_NAME: newCreationFlowEvents.clickCancelExperimentModal.state,
    });
    onCancel?.();
  };

  return (
    <BaseModal
      centered
      title={`${isUninitializedForm ? 'Create' : 'Edit'} Experiment`}
      onOk={handleEditExperiment}
      onCancel={handleCancel}
      okText="Save"
      okButtonProps={{
        size: 'small',
        disabled:
          isCloneMode || isEditMode
            ? initializedCloneExperiment && !contentChanged
            : !contentChanged,
      }}
      cancelButtonProps={{
        size: 'small',
      }}
      closable={!isUninitializedForm}
      maskClosable={!isUninitializedForm}
      width={720}
    >
      <Form
        layout="vertical"
        form={experimentMetadataForm}
        initialValues={initialMetadataValues}
      >
        <EditExperimentContentWrapper id="exp-creation-initial-form">
          <EditExperimentContentSection>
            <EditExperimentContentSectionTitle>
              Main details
            </EditExperimentContentSectionTitle>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Please enter your name',
                },
                {
                  required: true,
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (IsAlphaNumericWithUnderscore(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('No special character allowed'),
                    );
                  },
                },
              ]}
              style={{ margin: 0 }}
            >
              <Input
                maxLength={100}
                showCount
                disabled={isEditMode}
                placeholder="Add a name for your experiment"
              />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Hypothesis (
                  <a
                    href={EXPERIMENT_SAMPLE_TEMPLATE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Sample Template
                  </a>
                  )
                </span>
              }
              name="description"
              rules={[
                {
                  required: true,
                  message: 'Please enter your description',
                },
              ]}
              style={{ margin: 0 }}
            >
              <Input.TextArea
                rows={6}
                maxLength={5000}
                showCount
                placeholder="Google Doc / Jira Ticket / Slack Thread link that describes your experiment design and results"
                style={{ borderRadius: 12 }}
              />
            </Form.Item>
          </EditExperimentContentSection>
          <EditExperimentContentSection>
            <Form.Item
              name="domain"
              label="Domain"
              required
              rules={[
                {
                  required: !experimentMetadataFormValues?.skipDomain,
                  message: 'Please select a domain',
                },
              ]}
              style={{ margin: 0 }}
            >
              <Select
                placeholder="Select a domain"
                allowClear
                showSearch
                options={domainValues}
                disabled={
                  experimentMetadataFormValues?.skipDomain || isEditMode
                }
              />
            </Form.Item>
            <Typography.Text>
              Assign by: {assignByFacetName || '-'}
            </Typography.Text>
            <Typography.Text type="secondary">
              The randomization facet for the selected domain.
            </Typography.Text>
            <div
              style={{
                borderRadius: 12,
                background: '#f5f5f5',
                padding: '8px 16px',
              }}
            >
              <Checkbox
                checked={experimentMetadataFormValues?.skipDomain}
                disabled={isEditMode}
                onChange={(e: CheckboxChangeEvent) => {
                  experimentMetadataForm.setFieldValue(
                    ExpFormField.DOMAIN,
                    undefined,
                  );
                  experimentMetadataForm.setFieldValue(
                    SKIP_DOMAIN_FIELD,
                    e.target.checked,
                  );
                }}
              >
                I’m not sure which domain I need, I would prefer to skip this
                step and select variables.
              </Checkbox>
            </div>
          </EditExperimentContentSection>
          <EditExperimentContentSection>
            <ConfigOwnershipSection>
              <SectionCollapse
                expandIconPosition="start"
                title={
                  <div>
                    Ownership{' '}
                    <Typography.Text
                      type="secondary"
                      style={{ fontWeight: 400 }}
                    >
                      (optional)
                    </Typography.Text>
                  </div>
                }
                ghost
                style={{ boxShadow: 'none' }}
                collapsible="header"
              >
                <Form.Item
                  label="Product Manager"
                  name="productManager"
                  style={{ marginBottom: 8 }}
                >
                  <OwnersAutoComplete
                    value={experimentMetadataForm.getFieldValue(
                      ExpFormField.PRODUCT_MANAGER,
                    )}
                    onChange={(value) =>
                      experimentMetadataForm.setFieldValue(
                        ExpFormField.PRODUCT_MANAGER,
                        value,
                      )
                    }
                    description="Select a Product Manager for your experiment"
                  />
                </Form.Item>
                <Form.Item
                  label="Product Analyst"
                  name="productAnalyst"
                  style={{ margin: 0 }}
                >
                  <OwnersAutoComplete
                    value={experimentMetadataForm.getFieldValue(
                      ExpFormField.PRODUCT_ANALYST,
                    )}
                    onChange={(value) =>
                      experimentMetadataForm.setFieldValue(
                        ExpFormField.PRODUCT_ANALYST,
                        value,
                      )
                    }
                    description="Select a Product Analyst for your experiment"
                  />
                </Form.Item>
              </SectionCollapse>
            </ConfigOwnershipSection>
          </EditExperimentContentSection>
        </EditExperimentContentWrapper>
      </Form>
    </BaseModal>
  );
};

const EditExperimentContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EditExperimentContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e8e8e8;
`;

const EditExperimentContentSectionTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #3d3d3d;
`;

const ConfigOwnershipSection = styled.div`
  & .ant-collapse .ant-collapse-content > .ant-collapse-content-box {
    padding: 16px 0px;
  }
  & > .ant-collapse > .ant-collapse-item > .ant-collapse-header {
    padding: 0px;
  }
`;

export default EditExperimentModal;
